import { ref } from "vue";
import { getContacts } from "@/modules/contact/infrastructure/adapters/contactService";
import { useAppToast } from "@/shared/composables/useAppToast";
import { logger } from "@/infrastructure/services/LoggerService";
const PAGE_SIZE = 50;
const ALL_RESULTS_PAGE_SIZE = 100;
const ALL_RESULTS_MAX = 500;
/**
 * Contact-picker data source for the donation detail tab's edit pickers. Owns the option list,
 * loading flag, total count (drives the "see all results" footer) and the `firstName lastName` label
 * formatting. Supports an optional org scope (the referent contact of an Organization donation is
 * constrained to the donor org). Mirrors {@link useSubscriptionOptions}; the current selection is
 * seeded from a known label rather than a by-id fetch.
 */
export function useContactOptions() {
    const { toastError } = useAppToast();
    const contacts = ref([]);
    const options = ref([]);
    const loading = ref(false);
    const totalCount = ref(0);
    let loaded = false;
    // Which org the list was loaded for (undefined = no scope). A scope change forces a reload, and the
    // see-all browser fetches within the same scope.
    let loadedForOrgId;
    function toOption(c) {
        return { value: c.id, label: `${c.firstName} ${c.lastName}`.trim() || c.id };
    }
    function prepend(option) {
        if (option.value && !options.value.some((o) => o.value === option.value)) {
            options.value = [option, ...options.value];
        }
    }
    function seedSelected(id, label) {
        if (!id || options.value.some((o) => o.value === id))
            return;
        options.value = [{ value: id, label: label?.trim() || id }, ...options.value];
    }
    async function load(params = {}) {
        if (loaded && loadedForOrgId === params.organisationId && !params.force) {
            seedSelected(params.ensureSelectedId, params.ensureSelectedLabel);
            return;
        }
        loading.value = true;
        try {
            const res = await getContacts({
                page: 1,
                pageSize: PAGE_SIZE,
                organisationId: params.organisationId,
            });
            contacts.value = res.result;
            totalCount.value = res.totalItems;
            options.value = res.result.map(toOption);
            seedSelected(params.ensureSelectedId, params.ensureSelectedLabel);
            loaded = true;
            loadedForOrgId = params.organisationId;
        }
        catch (err) {
            logger.error("useContactOptions: failed to load contacts", err);
            toastError("contact.loadError");
        }
        finally {
            loading.value = false;
        }
    }
    function search(query) {
        const q = query.trim().toLowerCase();
        options.value = (!q
            ? contacts.value
            : contacts.value.filter((c) => `${c.firstName} ${c.lastName}`.toLowerCase().includes(q))).map(toOption);
    }
    /** Fetches a larger org-scoped slice for the "see all results" browser, client-filtered by query. */
    async function fetchAll(query) {
        const all = [];
        let page = 1;
        let total = Infinity;
        while (all.length < total && all.length < ALL_RESULTS_MAX) {
            const res = await getContacts({
                page,
                pageSize: ALL_RESULTS_PAGE_SIZE,
                organisationId: loadedForOrgId,
            });
            all.push(...res.result);
            total = res.totalItems;
            if (res.result.length === 0)
                break;
            page++;
        }
        const q = query.trim().toLowerCase();
        return all
            .slice(0, ALL_RESULTS_MAX)
            .filter((c) => !q || `${c.firstName} ${c.lastName}`.toLowerCase().includes(q))
            .map(toOption);
    }
    return { options, loading, totalCount, load, search, fetchAll, prepend };
}
